import { useEffect } from 'react';
import { authAPI, resumeSession } from '../api/client';

// 세션 4시간 + **활동 시 연장**(마스터 2026-08-20).
//
// 🔴 "요청이 오면 연장" 으로 만들면 안 된다 — 사이드바가 45초·60초마다 알림을 폴링하기 때문에
// 탭만 열어두면 세션이 영원히 유지된다. 그래서 연장 조건은 **사용자 조작**이다.
//
// 동작: 1분마다 확인해서 ① 마지막 갱신 이후 조작이 있었고 ② 그 조작이 최근 10분 안이면
// /auth/refresh 로 새 토큰을 받는다. 자리를 비우면 갱신이 멈추고 마지막 조작 +4시간에 끊긴다.
//
// 🔴 예외 = sid 클레임이 있는 토큰(모바일 '로그인 유지'). 이 토큰은 조작이 없어도 **만료 5분
// 전부터** resume 으로 미리 교체한다 — 회의 녹음처럼 화면을 안 만지는 사용에서 만료가 화면을
// 끊지 않게, 그리고 탭이 열려 있는 동안은 미사용 30일이 차지 않게 한다
// (/auth/refresh 는 idle 을 밀지 않는다 — M1b).
const REFRESH_INTERVAL_MS = 10 * 60 * 1000;   // 최소 갱신 간격(= 활동 인정 창)
const TICK_MS = 60 * 1000;
// sid 토큰을 미리 교체하기 시작하는 시점(만료 전 남은 시간).
const PRESWAP_LEAD_MS = 5 * 60 * 1000;
// 탭마다 마운트 때 한 번 정하는 지연(0~60초) — 여러 탭이 같은 초에 resume 으로 몰리지 않게.
const PRESWAP_JITTER_MS = 60 * 1000;

function expOf(token) {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp ? payload.exp * 1000 : 0;
  } catch {
    return 0;
  }
}

// 🔴 sid 가 있으면 모바일 지속 세션(refresh 쿠키로 되살아난다) = 미리 교체 대상.
//    sid 가 없으면(PC·옛 토큰) 아래 조작 기반 연장만 하던 기존 동작 그대로다.
function sidOf(token) {
  try {
    const part = String(token || '').split('.')[1];
    const claims = JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/')));
    return claims.sid === undefined ? null : claims.sid;
  } catch {
    return null;
  }
}

export default function useSessionKeepAlive() {
  useEffect(() => {
    let lastActivity = 0;                 // 마지막 사용자 조작
    let lastRefresh = Date.now();         // 페이지 로드 시점을 기준으로 시작
    let busy = false;
    // 이 탭의 미리 교체 지연 — 마운트 때 한 번만 정한다(여러 탭이 같은 초에 몰리지 않게).
    const jitter = Math.floor(Math.random() * PRESWAP_JITTER_MS);
    // 🔴 확정 거절(401·403)을 본 토큰. 쿠키가 저장되지 않는 환경(HTTP 접속·쿠키를 막는 인앱
    //    브라우저)에서는 sid 토큰은 있고 쿠키는 없다 — 그 토큰은 아래 조작 기반 /auth/refresh
    //    경로로 되돌아간다(끊으면 480분마다 로그아웃되는 후퇴가 된다). 다시 시도하지 않는다.
    let preswapDenied = null;

    const mark = () => { lastActivity = Date.now(); };
    const events = ['pointerdown', 'keydown', 'wheel', 'touchstart'];
    events.forEach((e) => window.addEventListener(e, mark, { passive: true }));

    // 지금 이 토큰을 미리 교체해야 하는가 — 네트워크 전에 전부 동기로 거른다.
    const shouldPreswap = (token, exp, now) => {
      if (typeof resumeSession !== 'function') return false;   // client 목킹 테스트
      if (sidOf(token) === null) return false;                 // PC·옛 토큰 — 기존 동작 그대로
      if (preswapDenied === token) return false;               // 확정 거절 → refresh 경로로
      return exp - now <= PRESWAP_LEAD_MS - jitter;
    };

    const preswap = async (token) => {
      // 🔴 부르기 직전에 저장소를 다시 읽는다 — 다른 탭이 이미 새 토큰을 넣었다면 회전형
      //    refresh 쿠키를 두 번 쓰게 되고, 재사용 탐지로 family 가 통째로 폐기된다.
      const fresh = localStorage.getItem('token');
      if (fresh && fresh !== token && expOf(fresh) - Date.now() > PRESWAP_LEAD_MS) return;
      const out = await resumeSession();
      // 토큰 저장은 resumeSession 한 곳에서만 한다(여기서 중복 저장하지 않는다).
      if (out && out.ok) { lastRefresh = Date.now(); return; }
      if (out && out.definitive) preswapDenied = token;
      // 비확정(503·429·네트워크)은 아무것도 남기지 않는다 — 다음 틱에 다시 시도한다.
    };

    const tick = async () => {
      const token = localStorage.getItem('token');
      if (!token || busy) return;
      const exp = expOf(token);
      if (!exp || Date.now() >= exp) return;        // 이미 만료면 App 의 60초 검사가 resume 한다
      const now = Date.now();
      // ① sid 토큰 = 조작과 무관하게 만료 전에 미리 교체한다(화면을 안 만지는 사용).
      if (shouldPreswap(token, exp, now)) {
        busy = true;
        try {
          await preswap(token);
        } catch {
          // resumeSession 은 결과 객체로 돌려주게 만들어져 있어 여기 오지 않지만, 목킹된
          // 환경에서도 죽지 않게 삼킨다(토큰은 그대로 남고 다음 틱이 다시 본다).
        } finally {
          busy = false;
        }
        return;
      }
      // ② 조작 기반 연장(sid 없는 토큰 · 미리 교체가 확정 거절된 토큰) — 기존 그대로.
      // 마지막 갱신 이후 조작이 있어야 하고, 그 조작이 최근이어야 한다(자리 비우면 연장 안 됨)
      if (lastActivity <= lastRefresh) return;
      if (now - lastActivity > REFRESH_INTERVAL_MS) return;
      if (now - lastRefresh < REFRESH_INTERVAL_MS) return;
      busy = true;
      try {
        const r = await authAPI.refresh();
        if (r?.data?.access_token) {
          localStorage.setItem('token', r.data.access_token);
          lastRefresh = Date.now();
        }
      } catch {
        // 401 이면 client.js 인터셉터가 로그인으로 보낸다 — 여기서 따로 처리하지 않는다.
      } finally {
        busy = false;
      }
    };

    const iv = setInterval(tick, TICK_MS);
    return () => {
      clearInterval(iv);
      events.forEach((e) => window.removeEventListener(e, mark));
    };
  }, []);
}
